import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { HardHat, LogIn, LogOut, MapPin, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PUBLIC site sign-in page — /site/:token (scanned from the QR poster).
 * Crew with a Fieldlink session get 1-tap sign-in; visitors/subbies fill
 * in name + company + phone. The device remembers its own sign-in so the
 * same phone shows a big SIGN OUT button.
 */
export default function SiteSignin() {
  const { token } = useParams();
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ name: "", company: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // {name} after successful action
  const myKey = `fl_site_signin_${token}`;
  const [mySignin, setMySignin] = useState(() => {
    try { return JSON.parse(localStorage.getItem(myKey) || "null"); } catch { return null; }
  });

  const load = () => {
    axios.get(`${API}/public/site/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "This sign-in link isn't active"));
  };
  useEffect(load, [token]);

  // If our remembered sign-in is no longer on site (office signed us out), forget it
  useEffect(() => {
    if (info && mySignin && !info.on_site.some((p) => p.id === mySignin.id)) {
      localStorage.removeItem(myKey);
      setMySignin(null);
    }
  }, [info]); // eslint-disable-line react-hooks/exhaustive-deps

  const remember = (rec) => {
    try { localStorage.setItem(myKey, JSON.stringify({ id: rec.id, name: rec.name })); } catch {}
    setMySignin({ id: rec.id, name: rec.name });
  };

  const workerSignin = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/site-signin/${token}/signin-worker`);
      remember(data);
      setDone({ name: data.name, action: "in" });
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Sign-in failed");
    } finally { setBusy(false); }
  };

  const visitorSignin = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/public/site/${token}/signin`, form);
      remember(data);
      setDone({ name: data.name, action: "in" });
      setForm({ name: "", company: "", phone: "" });
      load();
    } catch (er) {
      setErr(er?.response?.data?.detail || "Sign-in failed");
    } finally { setBusy(false); }
  };

  const signout = async (signinId, name) => {
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/public/site/${token}/signout`, { signin_id: signinId });
      if (mySignin?.id === signinId) {
        localStorage.removeItem(myKey);
        setMySignin(null);
      }
      setDone({ name, action: "out" });
      load();
    } catch (er) {
      setErr(er?.response?.data?.detail || "Sign-out failed");
    } finally { setBusy(false); }
  };

  if (err && !info) {
    return (
      <div className="min-h-screen bg-[#09090B] text-white flex items-center justify-center p-6">
        <div className="text-center" data-testid="site-signin-error">
          <HardHat className="w-10 h-10 text-[#FF5F15] mx-auto mb-3" />
          <div className="font-display font-black uppercase text-xl">{err}</div>
        </div>
      </div>
    );
  }
  if (!info) return <div className="min-h-screen bg-[#09090B]" />;

  return (
    <div className="min-h-screen bg-[#09090B] text-white" data-testid="site-signin-page">
      <div className="max-w-md mx-auto px-5 py-8">
        {/* Header */}
        <div className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 bg-[#FF5F15] flex items-center justify-center">
            <HardHat className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-display font-black text-lg">SITE SIGN-IN</span>
        </div>
        <div className="label-tag !text-[#FF5F15] mb-1">/ {info.org_name}</div>
        <h1 className="font-display font-black text-2xl uppercase tracking-tight leading-tight" data-testid="site-signin-job-title">
          {info.job_title}
        </h1>
        {info.site_address && (
          <div className="flex items-center gap-1.5 text-zinc-400 text-sm mt-1">
            <MapPin className="w-3.5 h-3.5" /> {info.site_address}
          </div>
        )}

        {done && (
          <div className={`mt-5 p-4 border-2 flex items-center gap-3 ${done.action === "in" ? "border-green-500 bg-green-500/10" : "border-zinc-500 bg-zinc-500/10"}`}
               data-testid="site-signin-done">
            <Check className="w-6 h-6 text-green-400 shrink-0" />
            <div className="font-bold">
              {done.name} signed {done.action === "in" ? "IN" : "OUT"}. {done.action === "in" ? "Stay safe out there." : "See ya."}
            </div>
          </div>
        )}
        {err && <div className="mt-4 text-sm text-red-400 font-bold" data-testid="site-signin-inline-error">{err}</div>}

        {/* My status */}
        {mySignin ? (
          <Button
            onClick={() => signout(mySignin.id, mySignin.name)}
            disabled={busy}
            className="mt-6 w-full h-14 rounded-sm bg-zinc-100 text-zinc-900 hover:bg-white font-display font-black uppercase tracking-wider text-base"
            data-testid="site-signout-self-btn"
          >
            <LogOut className="w-5 h-5 mr-2" /> Sign out — {mySignin.name.split(" ")[0]}
          </Button>
        ) : user ? (
          <Button
            onClick={workerSignin}
            disabled={busy}
            className="mt-6 w-full h-14 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] font-display font-black uppercase tracking-wider text-base"
            data-testid="site-signin-worker-btn"
          >
            <LogIn className="w-5 h-5 mr-2" /> Sign in as {user.name.split(" ")[0]}
          </Button>
        ) : (
          <form onSubmit={visitorSignin} className="mt-6 space-y-3" data-testid="site-signin-visitor-form">
            <div className="label-tag !text-zinc-400">/ Visitor / subbie sign-in</div>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="Your name *" className="h-12 rounded-sm bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500"
                   data-testid="site-signin-name-input" />
            <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                   placeholder="Company" className="h-12 rounded-sm bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500"
                   data-testid="site-signin-company-input" />
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                   placeholder="Phone" type="tel" className="h-12 rounded-sm bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500"
                   data-testid="site-signin-phone-input" />
            <Button type="submit" disabled={busy || form.name.trim().length < 2}
                    className="w-full h-14 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] font-display font-black uppercase tracking-wider text-base"
                    data-testid="site-signin-visitor-btn">
              <LogIn className="w-5 h-5 mr-2" /> Sign in to site
            </Button>
          </form>
        )}

        {/* On site now */}
        <div className="mt-8">
          <div className="flex items-baseline justify-between mb-2">
            <div className="label-tag !text-zinc-400">/ On site now</div>
            <span className="font-mono text-xs text-zinc-500">{info.on_site.length}</span>
          </div>
          {info.on_site.length === 0 ? (
            <div className="text-sm text-zinc-500 italic" data-testid="site-onsite-empty">Nobody signed in.</div>
          ) : (
            <div className="space-y-1.5" data-testid="site-onsite-list">
              {info.on_site.map((p) => (
                <div key={p.id} className="flex items-center gap-3 p-3 bg-zinc-900 border border-zinc-800">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${p.person_type === "worker" ? "bg-[#FF5F15]" : "bg-green-500"}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold truncate">{p.name}</div>
                    {p.company && <div className="text-xs text-zinc-500 truncate">{p.company}</div>}
                  </div>
                  <button type="button" onClick={() => signout(p.id, p.name)}
                          className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 hover:text-white px-2 py-1 border border-zinc-700 hover:border-zinc-400"
                          data-testid={`site-signout-${p.id}`}>
                    Sign out
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-10 text-center text-[10px] font-mono uppercase tracking-widest text-zinc-600">
          Powered by fieldlink.co.nz
        </div>
      </div>
    </div>
  );
}
